/**
 * `hero_slider` — the store's first screen (spec 42 R2, R13; design 43 SF2).
 *
 * The a11y contract is the hard part, and it is deliberate:
 *
 *  - **Autoplay never runs under `prefers-reduced-motion: reduce`.** Checked at
 *    mount *and* subscribed to, so toggling the OS setting takes effect without
 *    a reload.
 *  - **Autoplay pauses on hover and on focus-within** — a keyboard user
 *    tabbing into a slide's CTA must not have it move out from under them.
 *  - A labelled pause/play control renders whenever autoplay is configured, so
 *    motion is always stoppable by a control, not just by a system setting.
 *  - Dots are real buttons with `aria-current`, and the active one differs by
 *    **shape as well as colour** (brand §10: colour is never the only signal).
 *
 * Single-slide is not a degenerate carousel: no dots, no autoplay, no controls,
 * no `aria-roledescription`. It is just a banner.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { HeroSlide, LinkTarget } from "@flint/api-contract/store-home";
import { SectionImage } from "./section-image.js";
import { resolveTarget, isExternal, type TargetContext } from "./resolve-target.js";

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

function SlideCta({
  label,
  href,
  target,
  primary,
  onImage,
}: {
  label: string;
  href: string;
  target: LinkTarget;
  primary: boolean;
  /** Over a photo the outlined button is light-on-scrim; on the textured
   *  fallback it sits on --bg and must take ink colours to stay legible. */
  onImage: boolean;
}) {
  const secondary = onImage
    ? { backgroundColor: "rgba(255,255,255,0.15)", color: "#ffffff", border: "1px solid rgba(255,255,255,0.35)" }
    : { backgroundColor: "transparent", color: "var(--ink)", border: "1px solid var(--line-2)" };
  return (
    <a
      href={href}
      {...(isExternal(target) ? { rel: "noopener noreferrer", target: "_blank" } : {})}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0.625rem 1.5rem",
        borderRadius: "var(--r-sm)",
        fontWeight: primary ? 600 : 500,
        fontSize: "0.9375rem",
        textDecoration: "none",
        ...(primary
          ? { backgroundColor: "var(--accent)", color: "var(--accent-fg)", border: "none" }
          : secondary),
      }}
    >
      {label}
    </a>
  );
}

export function HeroSlider({
  slides,
  autoplayMs,
  ctx,
}: {
  slides: HeroSlide[];
  autoplayMs: number;
  ctx: TargetContext;
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const regionRef = useRef<HTMLDivElement>(null);

  const multi = slides.length > 1;
  const autoplayWanted = multi && autoplayMs > 0;
  const autoplayActive = autoplayWanted && !reducedMotion && !paused;

  const go = useCallback(
    (next: number) => setIndex(((next % slides.length) + slides.length) % slides.length),
    [slides.length],
  );

  useEffect(() => {
    if (!autoplayActive) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % slides.length), autoplayMs);
    return () => window.clearInterval(id);
  }, [autoplayActive, autoplayMs, slides.length]);

  const current = slides[index];
  if (!current) return null;

  // Resolve CTAs before render so the container knows whether any survived.
  const liveCtas = current.ctas.flatMap((cta) => {
    const href = resolveTarget(cta.target, ctx);
    return href ? [{ ...cta, href }] : [];
  });

  return (
    <section
      ref={regionRef}
      className="flint-hero"
      aria-label="Store highlights"
      {...(multi ? { "aria-roledescription": "carousel" } : {})}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      // Focus-within pause: a keyboard user inside a slide keeps the slide.
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(e) => {
        if (!regionRef.current?.contains(e.relatedTarget as Node | null)) setPaused(false);
      }}
      style={{ position: "relative", backgroundColor: "var(--bg)" }}
    >
      <div style={{ position: "relative" }}>
        <SectionImage
          src={current.imageUrl ?? null}
          alt=""
          aspectRatio="var(--flint-hero-ratio, 4 / 3)"
          eager={index === 0}
          radius="0"
        />

        {/* Scrim only over a real image — on the textured fallback the text
            sits on --bg and takes ink colours instead (design 43 SF2). */}
        {current.imageUrl ? (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0) 65%)",
            }}
          />
        ) : null}

        <div className="flint-hero-body">
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 600,
              lineHeight: 1.1,
              margin: 0,
              color: current.imageUrl ? "#ffffff" : "var(--ink)",
              fontSize: "var(--flint-hero-title, 1.875rem)",
            }}
          >
            {current.headline}
          </h1>
          {current.subhead ? (
            <p
              style={{
                marginTop: "0.5rem",
                fontSize: "0.9375rem",
                color: current.imageUrl ? "rgba(255,255,255,0.9)" : "var(--muted)",
                maxWidth: "44ch",
              }}
            >
              {current.subhead}
            </p>
          ) : null}
          {/* R3: dangling targets drop their CTA — and when every CTA drops,
              the container goes with them rather than leaving phantom space. */}
          {liveCtas.length > 0 ? (
            <div className="flint-hero-ctas">
              {liveCtas.map((cta, i) => (
                <SlideCta
                  key={`${cta.label}-${i}`}
                  label={cta.label}
                  href={cta.href}
                  target={cta.target}
                  primary={i === 0}
                  onImage={!!current.imageUrl}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {multi ? (
        <div className="flint-hero-controls">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => go(i)}
              aria-label={`Slide ${i + 1} of ${slides.length}`}
              aria-current={i === index ? "true" : undefined}
              style={{
                // Active differs in WIDTH as well as colour — shape is the
                // non-colour signal (brand §10).
                width: i === index ? 24 : 8,
                height: 8,
                padding: 0,
                borderRadius: 999,
                border: "none",
                cursor: "pointer",
                backgroundColor: i === index ? "var(--accent)" : "var(--line-2)",
                transition: "width 200ms ease-out",
              }}
            />
          ))}
          {/* AC24 requires the control to be PRESENT under reduced motion — a
              buyer who has asked for less motion still gets the affordance, and
              the composer promises them one. Only "autoplay off entirely"
              removes it. (Design 43 SF2 said otherwise; the spec's AC wins.) */}
          {autoplayWanted ? (
            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              aria-label={paused ? "Play slideshow" : "Pause slideshow"}
              style={{
                marginInlineStart: "0.5rem",
                width: 24,
                height: 24,
                display: "grid",
                placeItems: "center",
                borderRadius: 999,
                border: "1px solid var(--line)",
                background: "var(--paper)",
                color: "var(--muted)",
                cursor: "pointer",
                fontSize: "0.625rem",
                lineHeight: 1,
              }}
            >
              {paused ? "▶" : "❚❚"}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
